import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setIsLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    mobile: location.state?.mobile || "",
    email: "",
    referralCode: "",
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) {
      errs.name = "Name must be at least 2 characters";
    }
    if (!form.mobile || form.mobile.length !== 10 || !/^\d{10}$/.test(form.mobile)) {
      errs.mobile = "Please enter a valid 10-digit mobile number";
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Please enter a valid email address";
    }
    if (form.referralCode && !/^[A-Za-z0-9]{6}$/.test(form.referralCode)) {
      errs.referralCode = "Referral code must be 6 alphanumeric characters";
    }
    return errs;
  };

  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "GET_LOCATION_INFO"
        })
      );
    } else {
      toast.error("Location service not available");
    }
  }, [])

  const handleChange = (field, value) => {
    if (field === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (field === "referralCode") {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiCall(
        "register",
        {
          name: form.name,
          number: form.mobile,
          email: form.email,
          referral_code: form.referralCode,
        },
        false
      );

      if (result.status === true) {
        toast.success("Registration successful! Please verify your OTP.");
        navigate("/verify-otp", { state: { mobile: form.mobile, token: result.data.otp_token, prevPage: 'register' } });
      } else {
        if (result.response_code === 4) {
          navigate(`/login`, { state: { mobile: form.mobile } });
        } else {
          toast.error(result.message || "Registration failed. Please try again.");
        }
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col mx-auto overflow-y-auto"
      style={{
        background: APP_THEME.bgColor,
        padding: "24px",
        maxWidth: "480px",
      }}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        disabled={loading}
        className="bg-transparent w-10 h-10 rounded-full flex items-center justify-center mb-6 transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          border: `1.5px solid ${APP_THEME.borderColor}`,
          color: APP_THEME.textPrimary,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>

      <h1
        className="text-2xl font-bold mb-2 text-left"
        style={{
          color: APP_THEME.textPrimary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        Create Account
      </h1>

      <p
        className="text-sm mb-8 text-left"
        style={{
          color: APP_THEME.textSecondary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        Join {APP_THEME.appName} today
      </p>

      <form onSubmit={handleSubmit} className="w-full max-w-[420px]">
        {/* Full Name */}
        <div className="mb-5">
          <div
            className="flex items-center overflow-hidden transition-colors duration-200"
            style={{
              border: `1px solid ${errors.name ? APP_THEME.errorColor : APP_THEME.borderColor}`,
              borderRadius: APP_THEME.borderRadiusInput,
              background: APP_THEME.bgColor,
            }}
          >
            <div
              className="px-3.5 py-3 flex items-center justify-center border-r w-[54px] box-border"
              style={{
                background: APP_THEME.surfaceColor,
                borderRightColor: APP_THEME.borderColor,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Full Name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              disabled={loading}
              className="flex-1 px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60"
              style={{
                fontFamily: APP_THEME.fontFamily,
                color: APP_THEME.textPrimary,
              }}
              onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = APP_THEME.primaryColor)}
              onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = errors.name ? APP_THEME.errorColor : APP_THEME.borderColor)}
            />
          </div>
          {errors.name && (
            <div className="text-xs mt-1" style={{ color: APP_THEME.errorColor, fontFamily: APP_THEME.fontFamily }}>
              {errors.name}
            </div>
          )}
        </div>

        {/* Mobile Number */}
        <div className="mb-5">
          <div
            className="flex items-center overflow-hidden transition-colors duration-200"
            style={{
              border: `1px solid ${errors.mobile ? APP_THEME.errorColor : APP_THEME.borderColor}`,
              borderRadius: APP_THEME.borderRadiusInput,
              background: APP_THEME.bgColor,
            }}
          >
            <div
              className="px-3.5 py-3 text-sm font-semibold border-r w-[54px] box-border flex items-center justify-center"
              style={{
                background: APP_THEME.surfaceColor,
                color: APP_THEME.textPrimary,
                fontFamily: APP_THEME.fontFamily,
                borderRightColor: APP_THEME.borderColor,
              }}
            >
              +91
            </div>
            <input
              type="tel"
              maxLength={10}
              placeholder="Mobile Number"
              value={form.mobile}
              onChange={(e) => handleChange("mobile", e.target.value)}
              readOnly={!!location.state?.mobile}
              disabled={loading}
              className="flex-1 px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60 read-only:opacity-70"
              style={{
                fontFamily: APP_THEME.fontFamily,
                color: APP_THEME.textPrimary,
              }}
            />
          </div>
          {errors.mobile && (
            <div className="text-xs mt-1" style={{ color: APP_THEME.errorColor, fontFamily: APP_THEME.fontFamily }}>
              {errors.mobile}
            </div>
          )}
        </div>

        {/* Email */}
        <div className="mb-5">
          <div
            className="flex items-center overflow-hidden transition-colors duration-200"
            style={{
              border: `1px solid ${errors.email ? APP_THEME.errorColor : APP_THEME.borderColor}`,
              borderRadius: APP_THEME.borderRadiusInput,
              background: APP_THEME.bgColor,
            }}
          >
            <div
              className="px-3.5 py-3 flex items-center justify-center border-r w-[54px] box-border"
              style={{
                background: APP_THEME.surfaceColor,
                borderRightColor: APP_THEME.borderColor,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <input
              type="email"
              placeholder="Email Address"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              disabled={loading}
              className="flex-1 px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60"
              style={{
                fontFamily: APP_THEME.fontFamily,
                color: APP_THEME.textPrimary,
              }}
              onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = APP_THEME.primaryColor)}
              onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = errors.email ? APP_THEME.errorColor : APP_THEME.borderColor)}
            />
          </div>
          {errors.email && (
            <div className="text-xs mt-1" style={{ color: APP_THEME.errorColor, fontFamily: APP_THEME.fontFamily }}>
              {errors.email}
            </div>
          )}
        </div>

        {/* Referral Code */}
        <div className="mb-3">
          <div
            className="flex items-center overflow-hidden transition-colors duration-200"
            style={{
              border: `1px solid ${errors.referralCode ? APP_THEME.errorColor : APP_THEME.borderColor}`,
              borderRadius: APP_THEME.borderRadiusInput,
              background: APP_THEME.bgColor,
            }}
          >
            <div
              className="px-3.5 py-3 flex items-center justify-center border-r w-[54px] box-border"
              style={{
                background: APP_THEME.surfaceColor,
                borderRightColor: APP_THEME.borderColor,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Referral Code"
              value={form.referralCode}
              onChange={(e) => handleChange("referralCode", e.target.value)}
              maxLength={6}
              disabled={loading}
              className="flex-1 px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60"
              style={{
                fontFamily: APP_THEME.fontFamily,
                color: APP_THEME.textPrimary,
              }}
              onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = APP_THEME.primaryColor)}
              onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = errors.referralCode ? APP_THEME.errorColor : APP_THEME.borderColor)}
            />
          </div>
          {errors.referralCode && (
            <div className="text-xs mt-1" style={{ color: APP_THEME.errorColor, fontFamily: APP_THEME.fontFamily }}>
              {errors.referralCode}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 border-none rounded-lg text-[15px] font-semibold mb-6 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed active:scale-97"
          style={{
            background: loading ? APP_THEME.primaryDark : APP_THEME.primaryColor,
            color: APP_THEME.textOnPrimary,
            fontFamily: APP_THEME.fontFamily,
          }}
          onMouseOver={(e) => {
            if (!loading) e.currentTarget.style.background = APP_THEME.primaryDark;
          }}
          onMouseOut={(e) => {
            if (!loading) e.currentTarget.style.background = APP_THEME.primaryColor;
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <div
                className="w-5 h-5 rounded-full animate-spin"
                style={{
                  border: `2px solid ${APP_THEME.textOnPrimary}`,
                  borderTop: `2px solid transparent`,
                }}
              />
              Creating Account...
            </div>
          ) : (
            "Create Account"
          )}
        </button>
      </form>

      <div className="flex-1" />

      <div
        className="w-full pt-10 text-xs text-center leading-relaxed"
        style={{
          color: APP_THEME.textSecondary,
          fontFamily: APP_THEME.fontFamily,
        }}
      >
        By creating, you agree to our{" "}
        <span
          onClick={() =>
              window.open(
                APP_THEME.policyUrls?.terms,
                "_blank",
                "noopener,noreferrer"
              )
            }
          className="cursor-pointer transition-opacity"
          style={{
            color: APP_THEME.primaryColor,
            opacity: loading ? 0.5 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          Terms
        </span>{" "}
        &{" "}
        <span
          onClick={() =>
              window.open(
                APP_THEME.policyUrls?.privacy,
                "_blank",
                "noopener,noreferrer"
              )
            }
          className="cursor-pointer transition-opacity"
          style={{
            color: APP_THEME.primaryColor,
            opacity: loading ? 0.5 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          Privacy
        </span>
      </div>

      <style>
        {`
          .active\\:scale-97:active {
            transform: scale(0.97);
          }
        `}
      </style>
      <Toaster />
    </div>
  );
}

export default Register;